import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  TextField, InputAdornment, Tabs, Tab, Avatar, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const productCategories = [
  { label: 'All Products', value: 'all' },
  { label: 'Personal Financing', value: 'personal' },
  { label: 'Credit Cards', value: 'credit_card' },
  { label: 'Housing Loans', value: 'housing' },
  { label: 'Property', value: 'property' },
];

// Map raw product types → tab category keys
const typeToCategory = (type) => {
  switch (type) {
    case 'personal_financing':
    case 'auto_financing':
      return 'personal';
    case 'credit_card':
      return 'credit_card';
    case 'housing_loan':
      return 'housing';
    case 'property':
      return 'property';
    default:
      return 'other';
  }
};

const bankColors = {
  'Al Rajhi Bank': '#0B3D2E',
  'Public Islamic Bank': '#D32F2F',
  'CIMB Bank': '#8B0000',
  'CIMB Islamic': '#8B0000',
  'Bank Rakyat': '#1565C0',
  'Alliance Bank': '#FF8F00',
  'RHB Bank': '#0D47A1',
  'RHB Islamic Bank': '#0D47A1',
  'OCBC Bank': '#C62828',
  'Maybank': '#FFD600',
  'Public Islamic': '#D32F2F',
  'iMoney Property': '#00B894',
};

export default function ProductListing() {
  const { user } = useAuth();
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [salary, setSalary] = useState('');
  const [refreshKey] = useState(0);
  const navigate = useNavigate();

  // Transform raw products from db into display shape
  const products = useMemo(() => {
    return db.products.getAll().map((p) => ({
      id: p.id,
      name: p.name,
      bank: p.bank || '—',
      category: typeToCategory(p.type),
      rate: p.profitRate ? `${p.profitRate}% p.a.` : 'Variable',
      maxAmount: p.maxAmount ? `RM ${p.maxAmount.toLocaleString()}` : '-',
      monthlyPayment: '-',
      tags: Array.isArray(p.features) ? p.features.slice(0, 3) : [],
    }));
    // eslint-disable-next-line
  }, [user, refreshKey]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (tab !== 'all' && p.category !== tab) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!p.name.toLowerCase().includes(s) && !p.bank.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [products, tab, search]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Hero Banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1B365D 0%, #2C5282 100%)',
        borderRadius: 4, p: 5, mb: 4, color: 'white', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', right: -50, top: -50, width: 200, height: 200, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', right: 50, bottom: -30, width: 150, height: 150, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.03)' }} />
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Best Islamic Personal Financing</Typography>
        <Typography variant="body1" sx={{ opacity: 0.85, mb: 3 }}>Compare. Apply. Reach your goals.</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'white' }} /></InputAdornment>,
            }}
            sx={{
              width: 300, '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 2, color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.7)' },
            }}
          />
          <TextField
            size="small"
            placeholder="Monthly salary (RM)"
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Typography sx={{ color: 'white', fontSize: '0.85rem' }}>RM</Typography></InputAdornment>,
            }}
            sx={{
              width: 200, '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 2, color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
              },
            }}
          />
        </Box>
      </Box>

      {/* Category Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
      >
        {productCategories.map((c) => (
          <Tab key={c.value} label={c.label} value={c.value} />
        ))}
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We found <strong>{filtered.length}</strong> product{filtered.length !== 1 ? 's' : ''} for you!
      </Typography>

      {/* Product Cards */}
      {filtered.length > 0 ? (
        <Grid container spacing={3}>
          {filtered.map((product) => (
            <Grid item xs={12} key={product.id}>
              <Card sx={{
                borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: '1px solid #E0E0E0',
                '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
                transition: 'box-shadow 0.2s',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: 60, height: 60, mx: 'auto', mb: 1,
                          backgroundColor: bankColors[product.bank] || '#1B365D',
                          fontSize: '0.7rem', fontWeight: 700,
                        }}
                      >
                        {product.bank.split(' ').map((w) => w[0]).join('').slice(0, 3)}
                      </Avatar>
                      <Typography variant="caption" color="text.secondary">{product.bank}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                        {product.tags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" sx={{ fontSize: '0.65rem', height: 22, backgroundColor: '#E3F2FD', color: '#1565C0' }} />
                        ))}
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1B365D' }}>
                        {product.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5}>
                      <Typography variant="caption" color="text.secondary">You Pay</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{product.monthlyPayment}</Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5}>
                      <Typography variant="caption" color="text.secondary">Profit Rate</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{product.rate}</Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5}>
                      <Typography variant="caption" color="text.secondary">Max Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{product.maxAmount}</Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5}>
                      <Button
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(`/apply?product=${product.id}`)}
                        sx={{
                          borderRadius: 2, textTransform: 'none', fontWeight: 600,
                          backgroundColor: '#1B365D', '&:hover': { backgroundColor: '#142847' },
                          width: '100%',
                        }}
                      >
                        Apply
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 2 }}>No data yet</Alert>
      )}
    </Box>
  );
}
